// @flow

import React from "react";

import Section from "../../components/Section";
import EditableForm from "../../components/EditableForm";
import FormRow from "../../components/FormRow";
import EditableField from "../../components/EditableField";
import * as formatters from "../../utils/formatters";

import {
  COMPANY__ROLE,
  PURPOSE_KIND,
  FINANCING_PRODUCT,
  AMOUNT,
  GUARANTEE_SUPPORT,
  FEDERAL_STATE,
  EQUITY_SUPPORT,
  OTHER_DESCRIPTION,
  FINANCING_DESCRIPTION,
  STRUCTURING_SUPPORT,
  PURPOSE_KIND__CORONA,
  CORONA_TOTAL_REVENUE,
  CORONA_PERSONNEL_COSTS,
  CORONA_LIQUIDITY_NEED
} from "./formFields";

import { mapRole, mapBoolean, mapBankAdvisorProducts, mapPurposeKind } from "./mappers";
import styles from "./Details.scss";

type InformationBankAdvisorProps = {
  initialValues: {
    amount: string,
    company_role: string,
    equity_support: boolean,
    federal_state: string,
    financing_description: string,
    guarantee_support: boolean,
    other_description: string,
    [FINANCING_PRODUCT]: {
      kind: string,
      value: string
    }[],
    [PURPOSE_KIND]: {
      kind: string,
      value: string
    }[],
    structuring_support: boolean
  }
};

const InformationBankAdvisor = ({ initialValues }: InformationBankAdvisorProps) => {
  const purposeKindLength = initialValues[PURPOSE_KIND] ? initialValues[PURPOSE_KIND].length : 0;
  const financingProductLength = initialValues[FINANCING_PRODUCT]
    ? initialValues[FINANCING_PRODUCT].length
    : 0;

  return (
    <Section title="Informationen zum Vorhaben" reducedMargin numbered>
      <EditableForm initialValues={initialValues} isLocked>
        <FormRow separator>
          <EditableField
            fieldControlProps={{
              name: COMPANY__ROLE,
              caption: () => "Kunde ist ein..."
            }}
            displayValue={mapRole}
          />

          <EditableField
            fieldControlProps={{
              name: FEDERAL_STATE,
              caption: () => "Investitionsort liegt in..."
            }}
          />
        </FormRow>
        <div className={styles.header}>MITTELVERWENDUNG</div>
        {purposeKindLength &&
          initialValues[PURPOSE_KIND].map(({ kind }, index) => (
            <FormRow key={kind} separator={index === purposeKindLength - 1}>
              <EditableField
                fieldControlProps={{
                  name: `purpose_kind[${index}].kind`,
                  caption: () => "Objekt"
                }}
                displayValue={mapPurposeKind}
              />
              <EditableField
                fieldControlProps={{
                  name: `purpose_kind[${index}].value`,
                  caption: () => "Betrag"
                }}
                displayValue={formatters.formatCurrency}
              />
            </FormRow>
          ))}
        <FormRow separator>
          <EditableField
            fieldControlProps={{
              name: AMOUNT,
              caption: () => "Investitionssumme"
            }}
            displayValue={formatters.formatCurrency}
          />
        </FormRow>
        <div className={styles.header}>MITTELHERKUNFT</div>
        {financingProductLength &&
          initialValues[FINANCING_PRODUCT].map(({ kind }, index) => (
            <FormRow key={kind} separator={index === financingProductLength - 1}>
              <EditableField
                fieldControlProps={{
                  name: `product_kind[${index}].kind`,
                  caption: () => "Finanzierung"
                }}
                displayValue={mapBankAdvisorProducts}
              />
              <EditableField
                fieldControlProps={{
                  name: `product_kind[${index}].value`,
                  caption: () => "Betrag"
                }}
                displayValue={formatters.formatCurrency}
              />
            </FormRow>
          ))}
        <FormRow separator>
          <EditableField
            fieldControlProps={{
              name: AMOUNT,
              caption: () => "Summe Finanzierungsstruktur"
            }}
            displayValue={formatters.formatCurrency}
          />
        </FormRow>
        {purposeKindLength &&
          initialValues[PURPOSE_KIND].some(({ kind }) => kind === PURPOSE_KIND__CORONA) && (
            <FormRow separator>
              <EditableField
                fieldControlProps={{
                  name: CORONA_TOTAL_REVENUE,
                  caption: () => "Jahresumsatz 2019"
                }}
                displayValue={formatters.formatCurrency}
              />
              <EditableField
                fieldControlProps={{
                  name: CORONA_PERSONNEL_COSTS,
                  caption: () => "Lohnkosten 2019"
                }}
                displayValue={formatters.formatCurrency}
              />
              <EditableField
                fieldControlProps={{
                  name: CORONA_LIQUIDITY_NEED,
                  caption: () => "Liquiditätsbedarf der nächsten 18 Monate"
                }}
                displayValue={formatters.formatCurrency}
              />
            </FormRow>
          )}
        <FormRow>
          <EditableField
            fieldControlProps={{
              name: FINANCING_DESCRIPTION,
              additionalInfo: "Beschreibung"
            }}
          />
        </FormRow>
        <FormRow>
          <EditableField
            fieldControlProps={{
              name: GUARANTEE_SUPPORT,
              additionalInfo:
                "Ist die Absicherung des Finanzierungsvorhabens durch die Bürgschaftsbank gewünscht?"
            }}
            displayValue={mapBoolean}
          />
        </FormRow>
        <FormRow>
          <EditableField
            fieldControlProps={{
              name: EQUITY_SUPPORT,
              additionalInfo:
                "Ist die Eigenkapitalstärkung durch Beteiligungskapital von der Mittelständischen Beteiligungsgesellschaft gewünscht?"
            }}
            displayValue={mapBoolean}
          />
        </FormRow>
        <FormRow>
          <EditableField
            fieldControlProps={{
              name: STRUCTURING_SUPPORT,
              additionalInfo:
                "Können wir Sie über die gewählte Finanzierungsform hinaus unterstützen?"
            }}
            displayValue={mapBoolean}
          />
        </FormRow>
        <FormRow>
          <EditableField
            fieldControlProps={{
              name: OTHER_DESCRIPTION,
              additionalInfo: "Sonstige Informationen"
            }}
          />
        </FormRow>
      </EditableForm>
    </Section>
  );
};

export default InformationBankAdvisor;
